import React, { useEffect, useState } from 'react';
import { requestDiscountAuthorization, validateDiscountAuthorization } from '../api';
import InlineLoading from './InlineLoading';

const formatDiscountLabel = ({ discountAmount, discountPercent }) => {
  if (typeof discountPercent === 'number') {
    return `${discountPercent}%`;
  }
  if (typeof discountAmount === 'number') {
    return `$${discountAmount.toLocaleString('de-DE')}`;
  }
  return 'N/A';
};

const DiscountAuthorizationModal = ({
  isOpen,
  onClose,
  branchId,
  cashRegisterId,
  discountContext,
  onAuthorized,
  onRequested,
  initialState,
}) => {
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [requested, setRequested] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setCode('');
      setExpiresAt(null);
      setRequested(false);
      setAttemptsRemaining(null);
      setIsLoading(false);
      setError('');
      return;
    }
    if (initialState?.requested) {
      setRequested(true);
      setExpiresAt(initialState.expiresAt || null);
      setAttemptsRemaining(
        typeof initialState.attemptsRemaining === 'number'
          ? initialState.attemptsRemaining
          : null
      );
    }
  }, [isOpen, initialState]);

  if (!isOpen) return null;

  const handleRequest = async () => {
    setError('');
    if (!branchId || !cashRegisterId) {
      setError('Debe seleccionar una caja antes de solicitar autorizacion.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await requestDiscountAuthorization(branchId, cashRegisterId, {
        discountAmount: discountContext?.discountAmount ?? null,
        discountPercent: discountContext?.discountPercent ?? null,
        reason: reason || null,
      });
      setRequested(true);
      setExpiresAt(response?.expiresAt || null);
      setAttemptsRemaining(
        typeof response?.attemptsRemaining === 'number' ? response.attemptsRemaining : null
      );
      onRequested?.(response);
    } catch (err) {
      setError(err.message || 'Error solicitando autorizacion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidate = async () => {
    setError('');
    if (!branchId || !cashRegisterId) {
      setError('Debe seleccionar una caja antes de validar.');
      return;
    }
    if (!code.trim()) {
      setError('Ingrese el codigo recibido.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await validateDiscountAuthorization(branchId, cashRegisterId, {
        code: code.trim(),
      });
      onAuthorized?.(response);
      onClose();
    } catch (err) {
      setError(err.message || 'Codigo invalido');
      if (typeof err.attemptsRemaining === 'number') {
        setAttemptsRemaining(err.attemptsRemaining);
      } else if (err.message && err.message.includes('invalid')) {
        setAttemptsRemaining((prev) => (typeof prev === 'number' ? Math.max(prev - 1, 0) : prev));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    await handleRequest();
  };

  const discountLabel = formatDiscountLabel({
    discountAmount: discountContext?.discountAmount,
    discountPercent: discountContext?.discountPercent,
  });

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[1000] flex justify-center items-start overflow-y-auto px-4 py-6"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Descuentos</p>
            <h2 className="text-lg font-semibold text-gray-900">Autorizacion requerida</h2>
            <p className="text-xs text-gray-500 mt-1">
              Solicita y valida el codigo del encargado.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div>
              Descuento solicitado: <strong>{discountLabel}</strong>
            </div>
            {discountContext?.label && (
              <div className="mt-1">
                Item: <strong>{discountContext.label}</strong>
              </div>
            )}
          </div>

          {!requested ? (
            <>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">
                  Motivo (opcional)
                </label>
                <textarea
                  className="mt-2 w-full border border-gray-300 rounded-lg p-2 text-sm"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Describe el motivo del descuento"
                />
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <button
                type="button"
                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                onClick={handleRequest}
                disabled={isLoading}
              >
                {isLoading ? <InlineLoading label="Enviando" /> : 'Solicitar codigo'}
              </button>
            </>
          ) : (
            <>
              <div className="text-sm text-gray-700">
                <p>Se envio un codigo al encargado.</p>
                {expiresAt && (
                  <p className="text-xs text-gray-500 mt-1">
                    Expira: {new Date(expiresAt).toLocaleTimeString('es-CL')}
                  </p>
                )}
                {typeof attemptsRemaining === 'number' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Intentos restantes: {attemptsRemaining}
                  </p>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">
                  Codigo
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="mt-2 w-full border border-gray-300 rounded-lg p-2 text-sm"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Ingresa el codigo"
                />
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex gap-2">
                <button
                  type="button"
                  className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60"
                  onClick={handleValidate}
                  disabled={isLoading}
                >
                  {isLoading ? <InlineLoading label="Validando" /> : 'Validar'}
                </button>
                <button
                  type="button"
                  className="flex-1 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-60"
                  onClick={handleResend}
                  disabled={isLoading}
                >
                  Reenviar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscountAuthorizationModal;
